package com.swetdot.swetgram.cache.shared

import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import com.swetdot.swetgram.cache.CacheQueries
import com.swetdot.swetgram.cache.SwetgramCache
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass

internal val KClass<SwetgramCache>.schema: SqlSchema<QueryResult.Value<Unit>>
  get() = SwetgramCacheImpl.Schema

internal fun KClass<SwetgramCache>.newInstance(driver: SqlDriver): SwetgramCache = SwetgramCacheImpl(driver)

private class SwetgramCacheImpl(
  driver: SqlDriver,
) : TransacterImpl(driver),
    SwetgramCache {
  override val cacheQueries: CacheQueries = CacheQueries(driver)

  public object Schema : SqlSchema<QueryResult.Value<Unit>> {
    override val version: Long
      get() = 10

    override fun create(driver: SqlDriver): QueryResult.Value<Unit> {
      driver.execute(null, """
          |CREATE TABLE cachedUser (
          |    id           TEXT NOT NULL PRIMARY KEY,
          |    username     TEXT NOT NULL,
          |    displayName  TEXT NOT NULL,
          |    avatarUrl    TEXT,
          |    bio          TEXT,
          |    location     TEXT,
          |    dateOfBirth  TEXT,
          |    showBirthYear INTEGER NOT NULL DEFAULT 1,
          |    isOnline     INTEGER NOT NULL DEFAULT 0,
          |    lastSeenAt   INTEGER,
          |    isDeleted    INTEGER NOT NULL DEFAULT 0,
          |    hasBlackBadge INTEGER NOT NULL DEFAULT 0
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE cachedChat (
          |    id            TEXT NOT NULL PRIMARY KEY,
          |    kind          TEXT NOT NULL,
          |    title         TEXT,
          |    avatarUrl     TEXT,
          |    unreadCount   INTEGER NOT NULL DEFAULT 0,
          |    isMuted       INTEGER NOT NULL DEFAULT 0,
          |    isArchived    INTEGER NOT NULL DEFAULT 0,
          |    isPinned      INTEGER NOT NULL DEFAULT 0,
          |    updatedAt     INTEGER NOT NULL,
          |    -- Persisted per chat and sent back on reconnect. IMPLEMENTATION_NOTES §5: without this a
          |    -- client can never learn that an OLD message was edited or deleted.
          |    changeCursor  TEXT
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE cachedChatMember (
          |    chatId TEXT NOT NULL,
          |    userId TEXT NOT NULL,
          |    PRIMARY KEY (chatId, userId)
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE cachedMessage (
          |    chatId               TEXT NOT NULL,
          |    clientId             TEXT NOT NULL,
          |    -- Null until the server acknowledges the send.
          |    serverId             TEXT,
          |    senderId             TEXT NOT NULL,
          |    kind                 TEXT NOT NULL,
          |    text                 TEXT,
          |    imageUrl             TEXT,
          |    voiceUrl             TEXT,
          |    voiceDurationMs      INTEGER,
          |    voiceWaveformJson    TEXT,
          |    replyToId            TEXT,
          |    replyToSenderId      TEXT,
          |    replyToSenderDisplayName TEXT,
          |    replyToText          TEXT,
          |    replyToKind          TEXT,
          |    -- SENDING / SENT / READ / FAILED. SENDING and FAILED never come from the server.
          |    state                TEXT NOT NULL,
          |    sentAt               INTEGER NOT NULL,
          |    editedAt             INTEGER,
          |    isDeleted            INTEGER NOT NULL DEFAULT 0,
          |    version              INTEGER NOT NULL DEFAULT 1,
          |    canEdit              INTEGER NOT NULL DEFAULT 0,
          |    canDeleteForEveryone INTEGER NOT NULL DEFAULT 0,
          |    reactionsJson        TEXT,
          |    picsoId              TEXT,
          |    picsoExpiresAt       TEXT,
          |    -- DECISIONS §5: "Retry uses the same identifiers while content is unchanged." The key is
          |    -- generated once, at first send, and reused for every retry -- so it must outlive the
          |    -- process, which means it belongs in the database rather than in memory.
          |    idempotencyKey       TEXT,
          |    retryCount           INTEGER NOT NULL DEFAULT 0,
          |    PRIMARY KEY (chatId, clientId)
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE cachedDiscoverFeed (
          |    feedKey             TEXT NOT NULL PRIMARY KEY,
          |    postsJson           TEXT NOT NULL,
          |    olderCursor         TEXT,
          |    newerCursor         TEXT,
          |    validatorRequestKey TEXT,
          |    validatorEtag       TEXT,
          |    fetchedAt           INTEGER NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, """
          |CREATE TABLE cachedDailyTasks (
          |    cacheKey  TEXT NOT NULL PRIMARY KEY,
          |    tasksJson TEXT NOT NULL,
          |    fetchedAt INTEGER NOT NULL
          |)
          """.trimMargin(), 0)
      driver.execute(null, "CREATE INDEX cachedMessage_timeline ON cachedMessage(chatId, sentAt DESC, clientId DESC)", 0)
      driver.execute(null, "CREATE INDEX cachedMessage_serverId ON cachedMessage(serverId)", 0)
      driver.execute(null, "CREATE INDEX cachedMessage_pending ON cachedMessage(state) WHERE state IN ('SENDING', 'FAILED')", 0)
      return QueryResult.Unit
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.Value<Unit> {
      if (oldVersion <= 1 && newVersion > 1) {
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN replyToSenderDisplayName TEXT", 0)
      }
      if (oldVersion <= 2 && newVersion > 2) {
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN reactionsJson TEXT", 0)
      }
      if (oldVersion <= 3 && newVersion > 3) {
        driver.execute(null, "ALTER TABLE cachedUser ADD COLUMN location TEXT", 0)
        driver.execute(null, "ALTER TABLE cachedUser ADD COLUMN dateOfBirth TEXT", 0)
      }
      if (oldVersion <= 4 && newVersion > 4) {
        driver.execute(null, "ALTER TABLE cachedUser ADD COLUMN showBirthYear INTEGER NOT NULL DEFAULT 1", 0)
      }
      if (oldVersion <= 5 && newVersion > 5) {
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN voiceUrl TEXT", 0)
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN voiceDurationMs INTEGER", 0)
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN voiceWaveformJson TEXT", 0)
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN replyToKind TEXT", 0)
      }
      if (oldVersion <= 6 && newVersion > 6) {
        driver.execute(null, """
            |CREATE TABLE cachedDiscoverFeed (
            |    feedKey             TEXT NOT NULL PRIMARY KEY,
            |    postsJson           TEXT NOT NULL,
            |    olderCursor         TEXT,
            |    newerCursor         TEXT,
            |    validatorRequestKey TEXT,
            |    validatorEtag       TEXT,
            |    fetchedAt           INTEGER NOT NULL
            |)
            """.trimMargin(), 0)
      }
      if (oldVersion <= 7 && newVersion > 7) {
        driver.execute(null, "ALTER TABLE cachedUser ADD COLUMN hasBlackBadge INTEGER NOT NULL DEFAULT 0", 0)
      }
      if (oldVersion <= 8 && newVersion > 8) {
        driver.execute(null, """
            |CREATE TABLE cachedDailyTasks (
            |    cacheKey  TEXT NOT NULL PRIMARY KEY,
            |    tasksJson TEXT NOT NULL,
            |    fetchedAt INTEGER NOT NULL
            |)
            """.trimMargin(), 0)
      }
      if (oldVersion <= 9 && newVersion > 9) {
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN picsoId TEXT", 0)
        driver.execute(null, "ALTER TABLE cachedMessage ADD COLUMN picsoExpiresAt TEXT", 0)
      }
      return QueryResult.Unit
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.Value<Unit> {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion + 1)
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion)
      }
      return QueryResult.Unit
    }
  }
}
